/**
 * @file interpret/components/useOutputDevices.ts
 * @brief The Mac's audio outputs, for Call mode: where your translation goes
 *        (Verblizr Microphone) and where you hear theirs (your headphones).
 *        Labels appear once the mic is allowed, as for inputs.
 *
 * @author DES
 * @date 03-10-2026
 */

import { useEffect, useState } from "react";
import type { OutputDevice } from "./callMicDevices";

export function useOutputDevices(enabled: boolean, reloadKey = 0): OutputDevice[] {
  const [devices, setDevices] = useState<OutputDevice[]>([]);
  useEffect(() => {
    if (!enabled || !navigator.mediaDevices?.enumerateDevices) return;
    let cancelled = false;
    const load = async () => {
      const all = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      if (cancelled) return;
      setDevices(
        all
          .filter((d) => d.kind === "audiooutput" && d.deviceId && d.deviceId !== "default")
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Output ${i + 1}` })),
      );
    };
    void load();
    navigator.mediaDevices.addEventListener?.("devicechange", load);
    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener?.("devicechange", load);
    };
  }, [enabled, reloadKey]);
  return devices;
}
